import React, { useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import { COLORS } from "../constants";

/**
 * Hotspot — floating, glowing marker. Same visual language for both the
 * 4 main landmarks and the 10 Points-of-Interest; POIs render slightly
 * smaller (scale=0.7) to keep the landmarks visually dominant while still
 * matching the glow + bob aesthetic so they're easy to find.
 */
export default function Hotspot({ data, onClick, isActive, scale = 1 }) {
  const ref = useRef();
  const [hovered, setHovered] = useState(false);

  // Bob + spin around the marker's own y position.
  const baseY = data.position[1];
  const bobAmount = 0.15 * scale;

  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime + (data.id ? data.id.charCodeAt(0) : 0);
    ref.current.position.y = baseY + Math.sin(t * 2) * bobAmount;
    ref.current.rotation.y = t * 0.6;
  });

  // Sizes derived from scale.
  const coreR = 0.5 * scale;
  const glowR = 0.8 * scale;
  const labelY = 1.6 * scale;
  const lightDist = 4 * scale;

  return (
    <group position={data.position}>
      <group
        ref={ref}
        onPointerOver={(e) => {
          e.stopPropagation();
          setHovered(true);
          document.body.style.cursor = "pointer";
        }}
        onPointerOut={() => {
          setHovered(false);
          document.body.style.cursor = "auto";
        }}
        onClick={(e) => {
          e.stopPropagation();
          onClick(data);
        }}
      >
        <mesh>
          <octahedronGeometry args={[coreR, 0]} />
          <meshStandardMaterial
            color={COLORS.goldBright}
            emissive={COLORS.gold}
            emissiveIntensity={hovered || isActive ? 1.6 : 0.9}
            metalness={0.7}
            roughness={0.25}
          />
        </mesh>
        <mesh>
          <sphereGeometry args={[glowR, 16, 16]} />
          <meshStandardMaterial
            color={COLORS.gold}
            transparent
            opacity={hovered ? 0.18 : 0.08}
            emissive={COLORS.gold}
            emissiveIntensity={0.4}
          />
        </mesh>
      </group>
      <pointLight intensity={0.4 * scale} distance={lightDist} color={COLORS.gold} />
      <Html
        center
        position={[0, labelY, 0]}
        style={{ pointerEvents: "none" }}
        distanceFactor={10}
      >
        <div
          data-testid={`hotspot-label-${data.id}`}
          style={{
            color: COLORS.ivory,
            background: "rgba(11,21,48,0.78)",
            padding: scale < 1 ? "4px 10px" : "6px 12px",
            borderRadius: 999,
            border: `1px solid ${COLORS.gold}`,
            fontFamily: "Georgia, serif",
            fontSize: scale < 1 ? 11 : 13,
            letterSpacing: 1,
            whiteSpace: "nowrap",
            textTransform: "uppercase",
            boxShadow: `0 0 18px rgba(230,180,80,0.35)`,
          }}
        >
          {data.label}
        </div>
      </Html>
    </group>
  );
}
